import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/** The deal another screen asked the Pipeline to open: `state.focusLoanId`
 *  (the Deal Locator drawer's "Open in Pipeline") or a `?loan=` link. */
export function focusLoanIdFrom(location: { state?: unknown; search?: string }): string | null {
  const state = location.state as { focusLoanId?: unknown } | null | undefined
  if (state && typeof state.focusLoanId === 'string' && state.focusLoanId.trim()) {
    return state.focusLoanId.trim()
  }
  const param = new URLSearchParams(location.search ?? '').get('loan')
  return param?.trim() || null
}

export type PipelineFocus = { id: string; found: boolean }

/**
 * Answers a request to open one deal, once the book has loaded.
 *
 * The Locator sent `focusLoanId` and nothing read it, so "Open in Pipeline"
 * landed on the first row (R7). `onResolve` hears whether the deal is on the
 * book — a dead or archived deal is not — and the request is then dropped
 * from the location, so Back or a refresh does not select it again over the
 * user's own choice.
 */
export function usePipelineFocus(
  loans: ReadonlyArray<{ id: string }>,
  ready: boolean,
  onResolve: (focus: PipelineFocus) => void,
): void {
  const location = useLocation()
  const navigate = useNavigate()
  const requested = focusLoanIdFrom(location)
  const handler = useRef(onResolve)
  handler.current = onResolve
  const handled = useRef<string | null>(null)

  useEffect(() => {
    if (!requested || !ready || handled.current === location.key) return
    handled.current = location.key
    handler.current({ id: requested, found: loans.some((loan) => loan.id === requested) })
    const params = new URLSearchParams(location.search)
    params.delete('loan')
    const search = params.toString()
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true, state: null },
    )
  }, [requested, ready, loans, location.key, location.pathname, location.search, navigate])
}
